import { type FC } from 'react';
import { PiChatCircle } from 'react-icons/pi';
import { FunnelStatusTerminalOutcome } from 'src/api/generated';
import type { PipelineStatusColumnProps } from 'src/modules/communications/types/pipeline/status-column.type';
import Loading from 'src/modules/shared/components/atoms/loading';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { DraggableBoardColumn } from 'src/modules/shared/components/molecules/board/column';
import { DraggableItem } from 'src/modules/shared/components/molecules/draggable-item';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { PropertyPreviewVisibilityScope } from '../../../../contexts/property-preview-visibility-scope';
import {
  useStatusColumnConversationCount,
  useStatusColumnConversations,
} from '../../../../hooks/pipeline/status-column-conversations';
import { useCollapsibleColumnTransition } from '../../../../hooks/use-collapsible-column-transition';
import { createPipelineItemId } from '../../../../utils/pipeline/dnd.utils';
import { ConversationCardRenderer } from '../../conversations/conversation-card-renderer';
import { ConversationCardSkeletonRenderer } from '../../conversations/conversation-card-skeleton-renderer';
import { CollapsedStatusRail } from './collapsed-status-rail';
import { PipelineStatusColumnHeader } from './status-header';

const terminalColumnBodyClasses = {
  [FunnelStatusTerminalOutcome.POSITIVE]: {
    base: '!bg-success-50',
    highlighted: '!bg-success-100',
  },
  [FunnelStatusTerminalOutcome.NEGATIVE]: {
    base: '!bg-secondary-200/60',
    highlighted: '!bg-secondary-200',
  },
};

/**
 * Renders one collapsible funnel column with visible conversation previews.
 * @param props - Funnel filters, selection, and layout callbacks.
 * @returns The conversation pipeline presentation.
 */
export const PipelineStatusColumn: FC<PipelineStatusColumnProps> = ({
  status,
  selectedFunnelId,
  searchTerm,
  selectedConversationId,
  onConversationSelect,
  onOpenContactProfile,
  isHighlighted = false,
  className = '',
  filters = [],
  sorts = [],
  isAiLinked = false,
  isCollapsed = false,
  onToggleCollapsed,
}) => {
  const { t } = useI18n();
  const bodyClasses = status.terminalOutcome
    ? terminalColumnBodyClasses[status.terminalOutcome]
    : undefined;
  const { showCollapsedLayer, showExpandedLayer } =
    useCollapsibleColumnTransition(isCollapsed);

  const { count: collapsedCount, loading: collapsedCountLoading } =
    useStatusColumnConversationCount({
      status,
      selectedFunnelId,
      searchTerm,
      filters,
      sorts,
      enabled: isCollapsed,
    });

  const { setRefs, conversations, loading, isFetchingNextPage, totalCount } =
    useStatusColumnConversations({
      status,
      selectedFunnelId,
      searchTerm,
      filters,
      sorts,
      enabled: !isCollapsed,
    });

  const count =
    (isCollapsed ? collapsedCount : totalCount) ??
    totalCount ??
    conversations.length;
  const countLoading = isCollapsed ? collapsedCountLoading : loading;
  return (
    <div
      className={classes(
        'relative flex-none h-full min-h-0 mx-2 pb-2 overflow-hidden transition-[width] duration-200 ease-out',
        isCollapsed ? 'w-[48px]' : 'w-[280px]',
        className
      )}
    >
      {showCollapsedLayer && (
        <div
          className={classes(
            'absolute inset-0 transition-all duration-200 ease-out',
            isCollapsed
              ? 'pointer-events-auto opacity-100 translate-x-0'
              : 'pointer-events-none opacity-0 -translate-x-2'
          )}
        >
          <CollapsedStatusRail
            status={status}
            count={count}
            countLoading={countLoading}
            isAiLinked={isAiLinked}
            isHighlighted={isHighlighted}
            onExpand={onToggleCollapsed}
          />
        </div>
      )}

      {showExpandedLayer && (
        <PropertyPreviewVisibilityScope active={!isCollapsed}>
          <div
            className={classes(
              'absolute inset-0 transition-all duration-200 ease-out',
              isCollapsed
                ? 'pointer-events-none opacity-0 translate-x-2'
                : 'pointer-events-auto opacity-100 translate-x-0'
            )}
          >
            <DraggableBoardColumn
              bodyClassName={
                bodyClasses?.[isHighlighted ? 'highlighted' : 'base']
              }
              setRefs={setRefs}
              isHighlighted={isHighlighted}
              className="!w-full !h-full !min-h-0 !mx-0 !pb-0"
              header={
                <PipelineStatusColumnHeader
                  status={status}
                  count={count}
                  countLoading={countLoading}
                  isAiLinked={isAiLinked}
                  onToggleCollapsed={onToggleCollapsed}
                />
              }
              items={conversations.map((c) => createPipelineItemId(c.id))}
            >
              <div className="flex h-full flex-col space-y-2 py-0.5">
                {loading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <ConversationCardSkeletonRenderer
                      key={index}
                      layout="pipeline"
                    />
                  ))
                ) : conversations.length === 0 ? (
                  <EmptyState
                    appearance="ghost"
                    title={t('communications.pipeline.empty.title')}
                    description={t('communications.pipeline.empty.description')}
                    icon={
                      <PiChatCircle className="text-primary-500 w-full h-full" />
                    }
                  />
                ) : (
                  conversations.map((conversation) => (
                    <DraggableItem
                      key={conversation.id}
                      id={createPipelineItemId(conversation.id)}
                      data={{
                        type: 'conversation',
                        conversation,
                        status,
                      }}
                      unstyled
                      className="rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary-500 focus-visible:outline-offset-2"
                    >
                      <ConversationCardRenderer
                        conversation={conversation}
                        isSelected={selectedConversationId === conversation.id}
                        onClick={() => onConversationSelect?.(conversation.id)}
                        onOpenDetails={onOpenContactProfile}
                        layout="pipeline"
                      />
                    </DraggableItem>
                  ))
                )}

                {isFetchingNextPage && (
                  <div className="pt-1 pb-2">
                    <Loading size="sm" variant="neutral" />
                  </div>
                )}
              </div>
            </DraggableBoardColumn>
          </div>
        </PropertyPreviewVisibilityScope>
      )}
    </div>
  );
};
